import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StatusBanner } from '@/components/StatusBanner';
import {
  createSkill,
  deleteSkill,
  listSkills,
  patchSkill,
  type AgentSkillRow,
} from '@/lib/agentSkillsApi';
import { canManageSkills } from '@/lib/roles';
import { useMe } from '@/lib/useMe';

const LIST_KEY = ['admin', 'agent-skills'] as const;
const NO_NODE = '';

/** Same slug rule as the product's PlatformSkills. */
const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/** A switch built on a checkbox — this app has no switch component. */
function EnabledSwitch(props: { checked: boolean; label: string; onChange: (next: boolean) => void }) {
  return (
    <input
      type="checkbox"
      role="switch"
      aria-label={props.label}
      checked={props.checked}
      onChange={(event) => props.onChange(event.target.checked)}
      className="h-4 w-4 accent-primary"
    />
  );
}

interface FormState {
  title: string;
  node_type: string;
  content: string;
  enabled: boolean;
}
const EMPTY_FORM: FormState = { title: '', node_type: NO_NODE, content: '', enabled: true };

/**
 * Global agent skills (the console twin of the product's Settings -> Agent
 * skills block). SUPERADMIN ONLY — the nav hides it and the API refuses every
 * other console role; a person who types the URL sees the refusal.
 */
export function SkillsPage() {
  const { data: me } = useMe();
  const allowed = canManageSkills(me?.role);
  const queryClient = useQueryClient();

  const query = useQuery({ queryKey: LIST_KEY, queryFn: listSkills, enabled: allowed, retry: false });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AgentSkillRow | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<AgentSkillRow | null>(null);
  const [notice, setNotice] = useState<{ kind: 'error' | 'success'; message: string } | null>(null);

  const skills = query.data?.skills ?? [];
  const nodes = query.data?.nodes ?? [];
  const nodeLabel = (type: string | null) =>
    type ? (nodes.find((n) => n.type === type)?.name ?? type) : null;

  const refresh = () => queryClient.invalidateQueries({ queryKey: LIST_KEY, exact: true });

  const save = useMutation({
    mutationFn: async () => {
      const title = form.title.trim();
      if (!title) throw new Error('A title is required');
      // slugify keeps a-z and 0-9 only, so a title in another script leaves nothing for the slug.
      const slug = editing?.slug || slugify(title);
      if (!slug) throw new Error('The title needs at least one Latin letter or digit: the slug is built from it.');
      const payload = {
        title,
        slug,
        content: form.content,
        node_type: form.node_type || null,
        enabled: form.enabled,
      };
      if (editing) await patchSkill(editing.id, payload);
      else await createSkill(payload);
      return editing ? 'Skill updated.' : 'Skill created.';
    },
    onSuccess: (message) => {
      setNotice({ kind: 'success', message });
      setDialogOpen(false);
      void refresh();
    },
    onError: (err) => setNotice({ kind: 'error', message: errorText(err, 'Failed to save') }),
  });

  const toggle = useMutation({
    mutationFn: ({ row, next }: { row: AgentSkillRow; next: boolean }) =>
      patchSkill(row.id, { enabled: next }),
    onSuccess: () => void refresh(),
    onError: (err) => setNotice({ kind: 'error', message: errorText(err, 'Failed to update') }),
  });

  const remove = useMutation({
    mutationFn: (row: AgentSkillRow) => deleteSkill(row.id),
    onSuccess: () => {
      setNotice({ kind: 'success', message: 'Skill deleted.' });
      setDeleteTarget(null);
      void refresh();
    },
    onError: (err) => {
      setDeleteTarget(null);
      setNotice({ kind: 'error', message: errorText(err, 'Failed to delete') });
    },
  });

  if (!allowed) {
    return (
      <div>
        <h1 className="text-card-title font-semibold text-fg-primary">Skills</h1>
        <p className="mt-2 text-body text-fg-secondary">Skills are for the superadmin role only.</p>
      </div>
    );
  }

  const openNew = () => {
    setNotice(null);
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };
  const openEdit = (row: AgentSkillRow) => {
    setNotice(null);
    setEditing(row);
    setForm({ title: row.title, node_type: row.node_type ?? NO_NODE, content: row.content, enabled: row.enabled });
    setDialogOpen(true);
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-card-title font-semibold text-fg-primary">Skills</h1>
          <p className="mt-1 max-w-2xl text-body text-fg-secondary">
            A skill teaches the agent how to use one node, or gives it general context. These are
            global skills, shared with MCP clients.
          </p>
        </div>
        <button
          type="button"
          onClick={openNew}
          disabled={!query.isSuccess}
          className="rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          New skill
        </button>
      </div>

      <div className="mt-4 space-y-3">
        <p className="rounded-card border border-border bg-surface-brand-subtle px-3 py-2 text-meta text-fg-primary">
          A change applies at once to every workspace on this environment and to MCP clients. It
          does not carry over to another environment: repeat it there.
        </p>
        {notice && !dialogOpen && <StatusBanner kind={notice.kind} message={notice.message} />}
        {query.isError && (
          <div className="flex flex-wrap items-center gap-3">
            <StatusBanner
              kind="error"
              message={`Could not load the skills: ${errorText(query.error, 'unknown error')}`}
            />
            <button
              type="button"
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
              className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
            >
              {query.isFetching ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}
        {query.isLoading && <p className="text-body text-fg-hint">Loading…</p>}
        {query.isSuccess && skills.length === 0 && (
          <p className="text-body text-fg-secondary">No global skills yet. Add one with New skill.</p>
        )}

        {skills.map((row) => (
          <section key={row.id} className="rounded-card border border-border bg-surface-field p-4">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-body font-medium text-fg-primary">{row.title || row.slug}</h2>
                  <span className="font-mono text-micro text-fg-hint">{row.slug}</span>
                  <span className="rounded-full bg-surface-brand-subtle px-2 py-0.5 text-micro font-medium text-fg-secondary">
                    {nodeLabel(row.node_type) ?? 'General context'}
                  </span>
                  {!row.enabled && (
                    <span className="rounded-full bg-state-hover px-2 py-0.5 text-micro font-medium text-fg-secondary">
                      Disabled
                    </span>
                  )}
                </div>
                <p className="mt-2 line-clamp-3 whitespace-pre-wrap rounded-card bg-state-hover p-2 font-mono text-micro text-fg-secondary">
                  {row.content || '(empty)'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <EnabledSwitch
                  checked={row.enabled}
                  label={`${row.title || row.slug} enabled`}
                  onChange={(next) => toggle.mutate({ row, next })}
                />
                <button
                  type="button"
                  onClick={() => openEdit(row)}
                  className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary hover:bg-state-hover"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(row)}
                  className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-destructive-soft-foreground hover:bg-state-hover"
                >
                  Delete
                </button>
              </div>
            </div>
          </section>
        ))}
      </div>

      {dialogOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={editing ? 'Edit skill' : 'New skill'}
          className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
        >
          <div className="flex max-h-full w-full max-w-3xl flex-col overflow-y-auto rounded-card border border-border bg-surface-field p-6">
            <h2 className="text-h3 font-semibold text-fg-primary">{editing ? 'Edit skill' : 'New skill'}</h2>
            <label htmlFor="skill-title" className="mt-4 text-meta font-medium text-fg-secondary">
              Title
            </label>
            <input
              id="skill-title"
              value={form.title}
              onChange={(event) => setForm((f) => ({ ...f, title: event.target.value }))}
              className="mt-1 w-full rounded-card border border-border bg-background p-2 text-body text-fg-primary"
            />
            <label htmlFor="skill-node" className="mt-3 text-meta font-medium text-fg-secondary">
              Node
            </label>
            <select
              id="skill-node"
              value={form.node_type}
              onChange={(event) => setForm((f) => ({ ...f, node_type: event.target.value }))}
              className="mt-1 w-full rounded-card border border-border bg-background p-2 text-body text-fg-primary"
            >
              <option value={NO_NODE}>General context (no specific node)</option>
              {nodes.map((n) => (
                <option key={n.type} value={n.type}>
                  {n.name}
                </option>
              ))}
            </select>
            <label htmlFor="skill-content" className="mt-3 text-meta font-medium text-fg-secondary">
              Content
            </label>
            <textarea
              id="skill-content"
              value={form.content}
              rows={12}
              onChange={(event) => setForm((f) => ({ ...f, content: event.target.value }))}
              placeholder="How to use this node: when, params, wiring, gotchas…"
              className="mt-1 w-full rounded-card border border-border bg-background p-2 font-mono text-micro text-fg-primary"
            />
            <label className="mt-3 flex items-center gap-2 text-body text-fg-primary">
              <EnabledSwitch
                checked={form.enabled}
                label="Enabled"
                onChange={(next) => setForm((f) => ({ ...f, enabled: next }))}
              />
              Enabled
            </label>
            {notice?.kind === 'error' && (
              <div className="mt-4">
                <StatusBanner kind="error" message={notice.message} />
              </div>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                disabled={save.isPending}
                className="rounded-card border border-border px-4 py-2 text-body font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => save.mutate()}
                disabled={save.isPending || !form.title.trim()}
                className="rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete skill"
          description={`Delete ${deleteTarget.title || deleteTarget.slug}? The agent and MCP clients of every workspace on this environment will stop receiving it.`}
          confirmLabel="Delete"
          busy={remove.isPending}
          onConfirm={() => remove.mutate(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
